import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { API_BASE } from '../apiBase';

const colors = {
  bg: '#f0f1f3',
  cardBg: '#ffffff',
  navy: '#071a52',
  greenDark: '#5cb83c',
  textPrimary: '#1a1a2e',
  textSecondary: '#6b7280',
  border: '#e2e4e8',
};

const API = API_BASE;

function getImageUrl(item) {
  const partNo = item.item?.no || '';
  const typeStr = item.item?.type || '';
  const remarks = item.remarks || '';
  const isMinifig = remarks.toUpperCase().startsWith('MF') ||
    typeStr === 'M' || typeStr === 'MINIFIG' ||
    partNo.toLowerCase().startsWith('fig-');
  const colorId = item.color_id || 0;
  if (isMinifig) return `https://img.bricklink.com/ItemImage/MN/0/${partNo}.png`;
  return `https://img.bricklink.com/ItemImage/PN/${colorId}/${partNo}.png`;
}

function QuickLookup() {
  const [params] = useSearchParams();
  const initialQ = params.get('q') || '';
  const [query, setQuery] = useState(initialQ);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchInv() {
      try {
        const res = await fetch(`${API}/api/inventories`);
        const json = await res.json();
        setInventory(json.data || []);
      } catch (err) { console.error(err); }
      setLoading(false);
    }
    fetchInv();
  }, []);

  const q = query.trim().toLowerCase();
  const results = q.length < 2 ? [] : inventory.filter(item => {
    const partNo = (item.item?.no || '').toLowerCase();
    const name = (item.item?.name || '').toLowerCase();
    const color = (item.color_name || '').toLowerCase();
    const bin = (item.remarks || '').toLowerCase();
    return partNo.includes(q) || name.includes(q) || color.includes(q) || bin.includes(q);
  }).slice(0, 50);

  return (
    <div style={{ minHeight: '100vh', background: colors.bg, padding: '20px', fontFamily: "'Outfit', sans-serif" }}>
      <PageHeader title="Quick Lookup" icon="⌕" />

      <input
        type="text"
        placeholder="Search by part #, name, color, or bin..."
        autoFocus
        value={query}
        onChange={e => setQuery(e.target.value)}
        style={{
          width: '100%', padding: '14px 18px',
          background: colors.cardBg, border: `1px solid ${colors.border}`,
          borderRadius: '6px', color: colors.textPrimary,
          fontFamily: "'Outfit', sans-serif", fontSize: '1rem',
          outline: 'none', boxSizing: 'border-box', marginBottom: '16px',
        }}
      />

      {loading && (
        <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '20px', fontSize: '0.85rem' }}>
          Loading inventory...
        </div>
      )}

      {!loading && q.length < 2 && (
        <div style={{ textAlign: 'center', color: colors.textSecondary, padding: '30px', fontSize: '0.85rem' }}>
          Type at least 2 characters to search.
        </div>
      )}

      {!loading && q.length >= 2 && (
        <div style={{ fontSize: '0.72rem', color: colors.textSecondary, marginBottom: '10px' }}>
          {results.length} match{results.length !== 1 ? 'es' : ''}{results.length === 50 ? ' (showing first 50)' : ''}
        </div>
      )}

      {!loading && results.map((item, i) => {
        const partNo = item.item?.no || '';
        const name = item.item?.name || '';
        const color = item.color_name || '';
        const bin = item.remarks || '(no bin)';
        const qty = item.quantity || 0;
        const price = parseFloat(item.unit_price || 0).toFixed(4);
        return (
          <div key={i} style={{
            background: colors.cardBg, border: `1px solid ${colors.border}`,
            borderRadius: '6px', padding: '12px',
            marginBottom: '8px',
            display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <img src={getImageUrl(item)} alt={name}
              style={{ width: '56px', height: '56px', objectFit: 'contain', background: '#f9fafb', borderRadius: '6px', padding: '4px', border: `1px solid ${colors.border}`, flexShrink: 0 }}
              onError={e => { e.target.style.opacity = '0.15'; }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <a href={`https://www.bricklink.com/v2/catalog/catalogitem.page?P=${partNo}`}
                target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '0.92rem', fontWeight: 800, color: colors.navy, textDecoration: 'none' }}>
                {partNo}
              </a>
              <div style={{ fontSize: '0.75rem', color: colors.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' }}>
                {name}
              </div>
              <div style={{ fontSize: '0.7rem', color: colors.textSecondary, marginTop: '2px' }}>
                {color}
              </div>
            </div>
            <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 800, color: colors.greenDark }}>{bin}</div>
              <div style={{ fontSize: '0.72rem', color: colors.textSecondary, marginTop: '2px' }}>x{qty} · ${price}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default QuickLookup;
